import type { DocArticle } from './documents.ts';

/** Running the deployment: alerts, scheduling, frameworks, people, packages. */

const ALERTS: DocArticle = {
  id: 'alerts',
  title: 'Alerts',
  summary: 'What raises one, the three deliberate silences, and how they are delivered.',
  sections: [
    {
      id: 'alert-kinds',
      heading: 'What raises an alert',
      paragraphs: [
        'Three kinds. A Regression is a control that was met at the client’s previous assessment and is not met now. Visibility lost is a control we could assess before and cannot now. A Permission we hold is a permission granted to DriftWatch in a client’s tenant that this version no longer asks for — nothing moved in the client, we stopped asking.',
      ],
    },
    {
      id: 'silences',
      heading: 'Three deliberate silences',
      paragraphs: [
        'An alert you learn to ignore is worse than no alert, so three things raise nothing at all.',
      ],
      bullets: [
        'A client’s first assessment. Everything is new; none of it is a change.',
        'A control that was already failing and still fails. It is in the report, and it is not news.',
        'A control we can no longer assess raises the quieter visibility alert, not a failure.',
      ],
    },
    {
      id: 'lifecycle',
      heading: 'Open, acknowledged, resolved',
      paragraphs: [
        'Acknowledging is the human step: it records that someone has picked the alert up, against the name they type. Resolving is not a human step — an alert is resolved by a later assessment showing the thing has gone, never by somebody deciding it looks better.',
        'A problem that persists keeps its first-seen date and moves its last-seen date forward, rather than raising a second alert every month.',
      ],
    },
    {
      id: 'delivery',
      heading: 'Delivery',
      paragraphs: [
        'Settings → Alert delivery posts alerts to one endpoint for every client you manage; each message names its client. With delivery off, alerts are still raised and still appear on the Alerts screen — nothing is sent anywhere.',
        'The endpoint must be https. A signing secret can be set, and is write-only: it can be replaced or removed, never read back. Each message is signed so your receiver can verify it came from your deployment.',
        'A minimum severity can be set. Visibility alerts are delivered whatever the floor says, because losing sight of a control is not less important when it happens to a low-severity one.',
        'No reports and no evidence are ever sent to the endpoint. A webhook receiver is usually a chat room or a ticket queue, which is not a place for a client’s configuration.',
      ],
    },
  ],
};

const SCHEDULING: DocArticle = {
  id: 'scheduling',
  title: 'Scheduling',
  summary: 'The cadence, what defers an assessment, and the difference between the two screens.',
  sections: [
    {
      id: 'cadence',
      heading: 'Cadence',
      paragraphs: [
        'Clients are re-assessed on a cadence, thirty days by default. A client can carry its own cadence, or be paused with a reason — a paused client stops accruing evidence, so the reason is what stops a pause outliving its cause.',
        'The cadence is measured from the last successful assessment, not the last attempt, so a client that keeps failing does not look freshly assessed.',
      ],
    },
    {
      id: 'two-screens',
      heading: 'Two screens, and only one of them acts',
      paragraphs: [
        'Schedule shows what the next sweep would do. Nothing on that screen starts an assessment.',
        'Admin → Sweep runs it. That screen says so plainly, and it is owner-only. Running it twice in a day does not double-assess anyone: a manual run and the scheduled tick collapse into one assessment.',
      ],
    },
    {
      id: 'deferral',
      heading: 'What defers an assessment',
      paragraphs: [
        'A sweep queues a limited number of clients — five per account by default, twenty-five across the platform — longest overdue first. Microsoft throttles per tenant, and one slow client must not become a backlog for everyone else. Anything beyond the limit is deferred to a following sweep, never dropped.',
        'Other reasons a client is skipped are shown with the sentence that explains them: not yet due, already running, not connected, paused, backing off after repeated failures, or the account being over its package allowance.',
      ],
    },
    {
      id: 'backoff',
      heading: 'Backing off',
      paragraphs: [
        'After a failure the next attempt is held back, and the hold doubles with each consecutive failure up to the cadence. An interrupted assessment is not counted as a failure, because it says nothing about the client.',
      ],
    },
  ],
};

const FRAMEWORKS: DocArticle = {
  id: 'frameworks',
  title: 'Frameworks',
  summary: 'Seven projections of one catalogue, and what a projection does not claim.',
  sections: [
    {
      id: 'seven',
      heading: 'Seven, from one catalogue',
      paragraphs: [
        'Reports can be projected against SOC 2, ISO/IEC 27001:2022 Annex A, UK and EU GDPR Article 32, Cyber Essentials, the CIS Critical Security Controls v8.1, the CIS Microsoft 365 Foundations Benchmark and NIST CSF 2.0.',
        'There is one control catalogue. A framework is a projection of it, never a second set of checks — so the same evidence answers to every framework a client cares about, and a criterion appears only where a control actually maps to it.',
      ],
    },
    {
      id: 'claims',
      heading: 'What a projection claims',
      paragraphs: [
        'Each framework carries its own disclaimer, printed in the report and shown above the progress tabs. They say the same thing in seven ways: this is tenant configuration, assessed by a tool, and it is not the certification.',
        'Criterion titles are DriftWatch summaries of each criterion’s subject, not the framework’s normative text. For the CIS benchmark in particular, recommendations are renumbered between major versions — check the version shown against the edition you are working from before acting on a number.',
      ],
    },
    {
      id: 'choosing',
      heading: 'Choosing them per client',
      paragraphs: [
        'The client’s Coverage tab decides which frameworks their report is projected against. Every control is assessed whatever you choose — an accountancy practice pursuing Cyber Essentials does not need thirteen ISO 27001 controls in front of its assessor.',
        '“Every framework, including any we add later” is a different state from ticking all seven today: the first follows the catalogue as it grows, the second does not. Order in the report is the catalogue’s, not the order you tick them.',
        'Where your package covers a subset of frameworks, those are the ones available, and changing that is a change of package.',
      ],
    },
  ],
};

const PEOPLE: DocArticle = {
  id: 'people',
  title: 'People and roles',
  summary: 'Three roles, what each can do, and why nobody is ever deleted.',
  sections: [
    {
      id: 'roles',
      heading: 'The three roles',
      paragraphs: [
        'Reading is not a capability: every signed-in person can read everything in the deployment. Roles decide what may be changed.',
      ],
      bullets: [
        'Owner — manages people and deployment settings, and does everything an engineer does. Only an owner can make someone else an owner.',
        'Engineer — the daily work: connecting clients, running assessments, acknowledging alerts, recording attestations.',
        'Viewer — reads everything and changes nothing.',
      ],
    },
    {
      id: 'accounts',
      heading: 'Accounts',
      paragraphs: [
        'Accounts are created by an owner in Settings → People. There is no self-service sign-up and no password reset by email, which is deliberate for a console holding several organisations’ security posture.',
        'People are suspended, never deleted. Attestations, acknowledgements and assessments carry the identity of whoever did them, and deleting a person would turn that record into a dangling reference.',
        'A password must be at least twelve characters and there are no composition rules. Length is what makes a password strong — a passphrase of three or four words beats anything shorter with symbols in it.',
      ],
    },
    {
      id: 'guards',
      heading: 'The guards',
      paragraphs: [
        'You cannot change your own role or suspend your own account; ask another owner. A deployment must keep at least one active owner, so the last one cannot be demoted or suspended — otherwise nobody could ever manage people again.',
        'Setting someone’s password, or signing them out, ends every session they have open. A role change takes effect on their next request, not their next sign-in.',
      ],
    },
  ],
};

const PACKAGE: DocArticle = {
  id: 'package',
  title: 'Your package',
  summary: 'What the limits cap, what happens at one, and what a limit never changes.',
  sections: [
    {
      id: 'limits',
      heading: 'What is capped',
      paragraphs: [
        'Settings → Billing shows what your account is allowed and what is in use, under what each client costs: connected clients, clients with scheduled assessment on, active operators, and the default assessments per client per calendar month and frameworks reports may cover. A client with its own plan is held to that plan instead of the two defaults.',
        'An account with no agreed package is uncapped. Unlimited is not the same as zero.',
      ],
    },
    {
      id: 'at-the-limit',
      heading: 'At a limit',
      paragraphs: [
        'The action is refused with a sentence saying what the package allows and what is in use. Nothing already collected is touched.',
        'A bulk import is all-or-nothing: if the whole import would not fit, none of it is applied, and the message says how much room there is. Splitting it is the remedy.',
        'An account that is over a limit rather than at it has its assessments stopped account-wide until the package or the estate changes.',
      ],
    },
    {
      id: 'never',
      heading: 'What a package never changes',
      paragraphs: [
        'A limit never changes a verdict. Every control is still evaluated for every client that is assessed at all, and a framework outside the package is a projection we do not print — not a check we did not make.',
      ],
    },
  ],
};

export const OPERATING: readonly DocArticle[] = [ALERTS, SCHEDULING, FRAMEWORKS, PEOPLE, PACKAGE];
